<script lang="ts" setup>
import { __ } from '@@/bits/intl'
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import { OzListRowSeparator } from '@@/library/v4/components/OzListRow.vue'
import OzPadletLogo, { OzPadletLogoColorScheme } from '@@/library/v4/components/OzPadletLogo.vue'
import OzToggle from '@@/library/v4/components/OzToggle.vue'
import { useLtiStore } from '@@/pinia/lti_store'
import LtiWallListRow from '@@/vuecomponents/LtiWallListRow.vue'
import { computed, onMounted, ref } from 'vue'

const ltiStore = useLtiStore()

const deeplinkForm = ref<HTMLFormElement | null>(null)
const jwtMessageInput = ref<HTMLFormElement | null>(null)
const doRemake = ref(false)
const makeGradebook = ref(true)

const deeplinkUrlSearchParams = computed((): Record<string, string> => {
  const hash = {}
  new URL(ltiStore.deeplinkUrl).searchParams.forEach((k, v) => {
    hash[k] = v
  })
  return hash
})

onMounted(() => {
  setTimeout(() => {
    ltiStore.setExpiryErrorScreen()
  }, ltiStore.deeplinkTimeoutSeconds * 1000)
})

function selectWall(wall): void {
  let message = doRemake.value ? wall?.embed_remake_message : wall?.embed_message
  if (makeGradebook.value) {
    message = doRemake.value ? wall?.embed_remake_grade_message : wall?.embed_grade_message
  }
  deeplinkSubmit(message as string)
}

function deeplinkSubmit(message: string): void {
  const form = deeplinkForm.value
  const jwtMessageInputRef = jwtMessageInput.value
  if (form == null || jwtMessageInputRef == null) return
  jwtMessageInputRef.value = message
  form.submit()
}
</script>

<template>
  <div class="flex flex-col p-3 phone:p-8">
    <div class="flex items-center justify-between mb-8">
      <div class="flex flex-col tablet-portrait:ms-4">
        <OzPadletLogo
          :color-scheme="OzPadletLogoColorScheme.Text300"
          :href="ltiStore.openDashboardUrl"
          target="_blank"
          :class="['w-[66px] tablet-portrait:w-[100px] mb-1.5']"
        />
        <span class="text-14-16 text-dark-text-200 dark:text-light-text-200">{{ ltiStore.tenantName }}</span>
      </div>

      <div class="flex flex-col items-end">
        <div class="flex items-center">
          <span class="text-17-22 text-dark-text-100 dark:text-light-text-100">{{ ltiStore.userDisplayName }}</span>
          <OzAvatar
            :height="20"
            :width="20"
            class="hidden phone:inline-block rounded-full ms-2"
            :src="ltiStore.avatar"
            :alt-text-name="ltiStore.username"
          />
        </div>
        <span class="text-14-16 text-dark-text-200 dark:text-light-text-200 phone:me-7">{{ ltiStore.username }}</span>
      </div>
    </div>

    <div class="flex justify-between phone:justify-end mb-8">
      <OzContainedButton
        :href="ltiStore.openDashboardUrl"
        target="_blank"
        :color-scheme="OzContainedButtonColorScheme.Secondary"
        class="w-full phone:w-auto"
        @click="ltiStore.alertNeedToReopen"
        >{{ __('Open dashboard') }}</OzContainedButton
      >
      <OzContainedButton
        :href="ltiStore.createNewPadletUrl"
        target="_blank"
        :color-scheme="OzContainedButtonColorScheme.Primary"
        class="w-full phone:w-auto ms-3"
        @click="ltiStore.alertNeedToReopen"
        >{{ __('Make a padlet') }}</OzContainedButton
      >
    </div>

    <div
      v-if="ltiStore.walls.length"
      class="flex items-center justify-between bg-light-ui-200 dark:bg-dark-ui-200 rounded-2.5xl px-4 py-3 mb-6"
    >
      <div class="flex flex-col me-4">
        <h3 class="text-17-22 text-dark-text-100 dark:text-light-text-100 mb-0.5">
          {{ __('Create a copy for each user') }}
        </h3>
        <div class="text-14-16 text-dark-text-200 dark:text-light-text-200">
          <span class="hidden tablet-portrait:inline-block">{{ __('Use this padlet as a template.') }}</span>
          <span>{{ __('Turn this off if you want all users to work on the same padlet.') }}</span>
        </div>
      </div>

      <OzToggle class="shrink-0" :value="doRemake" @input="doRemake = !doRemake" />
    </div>

    <div
      v-if="ltiStore.walls.length"
      class="flex items-center justify-between bg-light-ui-200 dark:bg-dark-ui-200 rounded-2.5xl px-4 py-3 mb-6"
    >
      <div class="flex flex-col me-4">
        <h3 class="text-17-22 text-dark-text-100 dark:text-light-text-100 mb-0.5">
          {{ __('Create a gradebook entry') }}
        </h3>
        <div class="text-14-16 text-dark-text-200 dark:text-light-text-200">
          <span class="hidden tablet-portrait:inline-block">{{
            __('Create a gradebook entry to sync grades to.')
          }}</span>
          <span>{{ __('Turn this off if you don’t want to sync grades to your LMS.') }}</span>
        </div>
      </div>
      <OzToggle class="shrink-0" :value="makeGradebook" @input="makeGradebook = !makeGradebook" />
    </div>

    <div v-if="ltiStore.walls.length" class="flex flex-col">
      <h3 class="text-17 text-dark-text-200 dark:text-light-text-200 ms-4 mb-1">{{ __('Please select a padlet') }}</h3>

      <div class="flex flex-col bg-light-ui-200 dark:bg-dark-ui-200 rounded-2.5xl px-4 py-2">
        <LtiWallListRow
          v-for="(wall, i) in ltiStore.walls"
          :key="wall.id"
          :wall="wall"
          :separator="i < ltiStore.walls.length - 1 ? OzListRowSeparator.Short : OzListRowSeparator.None"
          @select="selectWall"
        />
      </div>
    </div>
    <div v-else class="flex flex-col bg-light-ui-200 dark:bg-dark-ui-200 rounded-2.5xl p-4">
      <p class="text-17-22 text-dark-text-100 dark:text-light-text-100">
        {{ __('No padlets available. Please create one.') }}
      </p>
    </div>

    <form ref="deeplinkForm" :action="ltiStore.deeplinkUrl" method="POST">
      <input v-for="(k, v) of deeplinkUrlSearchParams" :key="k" type="hidden" :name="k" :value="v" />
      <input ref="jwtMessageInput" type="hidden" name="JWT" />
    </form>
  </div>
</template>
